import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { IPoseData } from '@interfaces/pose.interface';
import { Tooltip } from 'antd';
import SkeletonCanvas from '../SkeletonCanvas';
import { scMixinPseudoBlock } from '@utils/emotion/mixins';
import { scAnimFadeIn } from '@utils/emotion/keyframes';
import classnames from 'classnames';

import { ReactComponent as IconCoords } from '@svg/icon-coords.svg';
import { useTranslation } from 'react-i18next';

interface FrameTimelineProps {
  duration: number;
  flattenedFrameData: IPoseData[];
  closestTime: number;
  setCurrentFrame: (data: number) => void;
}

let resizeTimerToken: number;
let nowFrameBorder = 0;

const FrameTimeline: React.FC<FrameTimelineProps> = ({
  duration,
  flattenedFrameData,
  closestTime,
  setCurrentFrame,
}) => {
  const { t } = useTranslation();
  const timelineRootElement = useRef<HTMLDivElement | null>(null);
  const [frames, setFrames] = useState<IPoseData[]>([]);
  const [isVideoLoaded, setVideoLoaded] = useState(false); // 돔 Load 여부
  const [timelineDimensionX, setTimelineDimensionX] = useState(0); // 타임라인 Width
  const frameWidth = 142;

  const calculateFrames = useCallback(() => {
    const visibleFrameQty = Math.ceil(timelineDimensionX / frameWidth); // +1 frame each Left and Right
    const currentFrameIndex = flattenedFrameData.findIndex((frame) => frame.time === closestTime);
    const borderFrameIndex = Math.floor(timelineDimensionX / frameWidth / 2);
    const tAsideFrameQty = Math.round(visibleFrameQty / 2);
    const tStartIndex = currentFrameIndex - borderFrameIndex;
    const tEndIndex = currentFrameIndex + tAsideFrameQty;
    let startIndex: number;
    let endIndex: number;

    if (tStartIndex < 0 && tEndIndex > currentFrameIndex + tAsideFrameQty * 2) {
      startIndex = 0;
      endIndex = flattenedFrameData.length - 1;
    } else if (tStartIndex < 0) {
      startIndex = 0;

      if (tEndIndex + Math.abs(tStartIndex) < flattenedFrameData.length) {
        endIndex = tEndIndex + Math.abs(tStartIndex);
      } else {
        endIndex = flattenedFrameData.length - 1;
      }
    } else if (tEndIndex > currentFrameIndex + tAsideFrameQty * 2) {
      endIndex = flattenedFrameData.length - 1;

      if (tStartIndex - (currentFrameIndex + tAsideFrameQty * 2) > 0) {
        startIndex = tStartIndex - (currentFrameIndex + tAsideFrameQty * 2);
      } else {
        startIndex = 0;
      }
    } else {
      startIndex = tStartIndex;
      endIndex = tEndIndex;
    }

    nowFrameBorder =
      currentFrameIndex < borderFrameIndex
        ? currentFrameIndex * frameWidth
        : borderFrameIndex * frameWidth;
    setFrames(flattenedFrameData.slice(startIndex, endIndex));
  }, [timelineDimensionX, flattenedFrameData, closestTime]);

  useEffect(() => {
    if (duration) setVideoLoaded(true);
  }, [duration]);

  useEffect(() => {
    if (isVideoLoaded && timelineRootElement.current) {
      setTimelineDimensionX(timelineRootElement.current.clientWidth);
    }

    const resizeHandler = () => {
      clearTimeout(resizeTimerToken);
      if (isVideoLoaded && timelineRootElement.current) {
        resizeTimerToken = window.setTimeout(() => {
          setTimelineDimensionX(timelineRootElement.current!.clientWidth);
        }, 50);
      }

      window.setTimeout(() => {
        if (
          timelineRootElement.current &&
          timelineDimensionX !== timelineRootElement.current.clientWidth
        ) {
          setTimelineDimensionX(timelineRootElement.current.clientWidth);
        }
      }, 300);
    };

    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [isVideoLoaded, calculateFrames, timelineDimensionX]);

  useEffect(() => {
    calculateFrames();
  }, [timelineDimensionX, calculateFrames]);

  return (
    <TimelineBlock>
      <div className="timeline-box">
        <i className="timeline-icon">
          <Tooltip
            title={t('timeline.icon.tooltip.coords')}
            placement="bottom"
            align={{ offset: [0, -6] }}
          >
            <IconCoords />
          </Tooltip>
        </i>
        {isVideoLoaded && (
          <div className="timeline-box-inner" ref={timelineRootElement}>
            <div
              className="timeline-box-border"
              style={{ transform: `translateX(${nowFrameBorder}px)` }}
            ></div>
            <div className="timeline-box-outer">
              {frames.map((frame) => (
                <div
                  className={classnames('frame-inner', {
                    'is-byuser': frame.data.keypoints.some((d) => d.byUser),
                    'is-column-even': frame.frameIdx % 2 === 0,
                  })}
                  key={frame.time}
                  onClick={() => setCurrentFrame(frame.time)}
                >
                  <SkeletonCanvas type="frame" currentFrameData={frame} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </TimelineBlock>
  );
};

const TimelineBlock = styled.div`
  display: block;
  height: 92px;

  .timeline-box {
    position: relative;
    height: 100%;
    padding: 5px 0 5px 95px;
    border: 1px solid #4f4f4f;
    border-top-width: 0;
  }

  .timeline-icon {
    position: absolute;
    left: 40px;
    top: 50%;
    transform: translateY(-50%);
  }

  .timeline-box-inner {
    overflow: hidden;
    position: relative;
    height: 80px;
    margin: 0 5px;
    background-color: #f5f5f5;
    white-space: nowrap;
  }

  .timeline-box-border {
    border: 5px solid rgb(129, 0, 255);
    box-sizing: border-box;
    position: absolute;
    z-index: 1;
    height: 80px;
    width: 142px;
  }

  .frame-inner {
    display: inline-block;
    position: relative;
    animation: ${scAnimFadeIn} 250ms ease;

    &.is-column-even::after {
      ${scMixinPseudoBlock};
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
      background-color: rgba(0, 0, 0, 0.1);
    }

    &::before {
      ${scMixinPseudoBlock};
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 2;
      border: 1px solid rgba(0, 0, 0, 0.1);
      background-color: rgba(0, 0, 0, 0);
      pointer-events: none;
      transition: background-color 200ms ease;
    }

    &:hover::before {
      background-color: rgba(0, 0, 0, 0.1);
    }

    &.is-byuser::before {
      background-color: rgba(129, 0, 255, 0.3);
    }

    & > div {
      position: static !important;
      width: 142px !important;
      height: 80px !important;
    }
  }
`;

export default FrameTimeline;
